/**
 * Create or scope a staff member.
 *
 * The role picker sets a template; the switches below narrow it further.
 * Choosing "Cashier" and assigning one till is the whole point -- it is
 * the shape of restriction trading businesses actually ask for.
 */
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, FormGroup, MenuItem, Stack,
  Switch, TextField, Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import {
  createStaff, fetchRoleTemplates, fetchStaff, updateStaff,
} from '../api/endpoints'
import { errorMessage } from '../api/client'
import { useAuth } from '../store/AuthContext'
import { useLedgers } from '../store/LedgerCache'
import { c } from '../theme'

interface Props { userId?: number; onClose: () => void; onSaved: () => void }

export default function StaffDialog({ userId, onClose, onSaved }: Props) {
  const { t } = useTranslation()
  const editing = !!userId
  const { ledgers } = useLedgers()
  const { me: session } = useAuth()
  const [error, setError] = useState<string | null>(null)

  // The companies the ADMIN can open: the only ones they may hand out,
  // and the only ones shown. A company the admin cannot see is never
  // listed, so its existence is not revealed either.
  const myCompanies = session?.companies ?? []
  const currentName = myCompanies.find(
    (x) => x.id === session?.company_id)?.name_en ?? ''


  const roles = useQuery({ queryKey: ['roles'], queryFn: fetchRoleTemplates })
  const staff = useQuery({
    enabled: editing, queryKey: ['staff'], queryFn: fetchStaff })

  const me = staff.data?.find((u) => u.id === userId)

  const [form, setForm] = useState({
    username: '', password: '', full_name: '', role: 'CLERK',
    restrict_to_own: false,
    allowed_cash_bank: [] as string[],
    allowed_parties: [] as string[],
    // A new user starts with the company the admin is working in, which
    // is what creating a user always meant.
    company_ids: (session ? [session.company_id] : []) as number[],
  })

  useEffect(() => {
    if (me) setForm({
      username: me.username, password: '', full_name: me.full_name ?? '',
      role: me.role, restrict_to_own: me.restrict_to_own,
      allowed_cash_bank: me.allowed_cash_bank,
      allowed_parties: me.allowed_parties,
      company_ids: me.company_ids ?? [],
    })
  }, [me])

  // By the group LINEAGE, never by display code. The old test --
  // group_code in ['CASH', 'BANK'] -- matched nothing once a company
  // switched to dot-notation, and only the two hard-coded ledger codes
  // kept the list from being empty. Same bug as the restricted pickers.
  const cashBank = useMemo(
    () => ledgers.filter((l) => (l.group_keys ?? [l.group_key])
      .some((k) => k === 'CASH' || k === 'BANK')), [ledgers])
  const parties = useMemo(
    () => ledgers.filter((l) => l.maintain_bill_wise), [ledgers])

  const perms = roles.data?.find((r) => r.role === form.role)?.permissions ?? []

  const save = useMutation({
    mutationFn: () => {
      const body = {
        role: form.role, restrict_to_own: form.restrict_to_own,
        allowed_cash_bank: form.allowed_cash_bank,
        allowed_parties: form.allowed_parties,
        company_ids: form.company_ids,
      }
      if (editing) return updateStaff(userId!, body)
      return createStaff({ ...body, username: form.username,
        password: form.password, full_name: form.full_name || undefined })
    },
    onSuccess: () => { onSaved(); onClose() },
    onError: (e) => setError(errorMessage(e)),
  })

  const set = (k: string, v: unknown) => setForm((f) => ({ ...f, [k]: v }))
  const isSelf = editing && userId === session?.id
  // The server refuses both of these too; saying so on the button is
  // cheaper than a round trip and an error the user has to read.
  const valid = form.company_ids.length > 0 &&
    (editing || (form.username.length >= 2 && form.password.length >= 8))

  const toggleCompany = (id: number, on: boolean) =>
    set('company_ids', on
      ? [...form.company_ids, id]
      : form.company_ids.filter((x) => x !== id))

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {editing ? form.username : t('settings.newUser')}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}
          onClose={() => setError(null)}>{error}</Alert>}
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {!editing && (
            <>
              <Stack direction="row" spacing={2}>
                <TextField label={t('auth.username')} value={form.username}
                  autoFocus sx={{ flex: 1 }}
                  onChange={(e) => set('username', e.target.value)} />
                <TextField label={t('auth.password')} type="password"
                  value={form.password} sx={{ flex: 1 }}
                  helperText={t('settings.passwordHint')}
                  onChange={(e) => set('password', e.target.value)} />
              </Stack>
              <TextField label={t('settings.fullName')} value={form.full_name}
                onChange={(e) => set('full_name', e.target.value)} />
            </>
          )}

          {/* Which companies this person may open. Shown only when there
              is more than one to choose from: with a single company the
              answer is always "that one", and a lone ticked box is noise. */}
          {myCompanies.length > 1 && (
            <Box>
              <Typography sx={{ fontSize: 12, color: c.inkMuted, mb: 0.5 }}>
                {t('settings.companyAccess')}
              </Typography>
              <FormGroup>
                {myCompanies.map((co) => {
                  // You cannot untick the company you are working in on
                  // your own account: the next click would say "no access".
                  const locked = isSelf && co.id === session?.company_id
                  return (
                    <FormControlLabel key={co.id}
                      control={<Checkbox size="small"
                        checked={form.company_ids.includes(co.id)}
                        disabled={locked}
                        onChange={(e) => toggleCompany(co.id, e.target.checked)} />}
                      label={<Typography sx={{ fontSize: 13.5 }}>{co.name_en}</Typography>} />
                  )
                })}
              </FormGroup>
              {form.company_ids.length === 0 && (
                <Typography sx={{ fontSize: 11.5, color: c.dangerInk }}>
                  {t('settings.needOneCompany')}
                </Typography>
              )}
            </Box>
          )}

          <TextField select label={t('settings.role')} value={form.role}
            onChange={(e) => set('role', e.target.value)}>
            {roles.data?.map((r) => (
              <MenuItem key={r.role} value={r.role}>{r.role}</MenuItem>
            ))}
          </TextField>

          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {perms.slice(0, 12).map((p) => (
              <Chip key={p} size="small" variant="outlined" label={p}
                sx={{ height: 20, fontSize: 10 }} />
            ))}
            {perms.length > 12 && (
              <Chip size="small" label={`+${perms.length - 12}`}
                sx={{ height: 20, fontSize: 10 }} />
            )}
          </Stack>

          <FormControlLabel
            control={<Switch checked={form.restrict_to_own}
              onChange={(e) => set('restrict_to_own', e.target.checked)} />}
            label={t('settings.ownRecordsOnly')} />

          {myCompanies.length > 1 && (
            <Typography sx={{ fontSize: 11.5, color: c.inkMuted, mt: 1 }}>
              {t('settings.limitsApplyTo', { company: currentName })}
            </Typography>
          )}

          <Autocomplete multiple size="small" options={cashBank}
            getOptionLabel={(l) => `${l.code} · ${l.name_en}`}
            value={cashBank.filter((l) => form.allowed_cash_bank.includes(l.code))}
            onChange={(_, v) => set('allowed_cash_bank', v.map((l) => l.code))}
            renderInput={(p) => <TextField {...p}
              label={t('settings.allowedTills')}
              helperText={t('settings.emptyMeansAll')} />} />

          <Autocomplete multiple size="small" options={parties}
            getOptionLabel={(l) => `${l.code} · ${l.name_en}`}
            value={parties.filter((l) => form.allowed_parties.includes(l.code))}
            onChange={(_, v) => set('allowed_parties', v.map((l) => l.code))}
            renderInput={(p) => <TextField {...p}
              label={t('settings.allowedParties')}
              helperText={t('settings.emptyMeansAll')} />} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" disabled={!valid || save.isPending}
          onClick={() => save.mutate()}>
          {save.isPending ? t('common.loading') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
